/**
 * Invite Admin — adds a co-design admin to a program that's In Design.
 * Same two paths as the Program Owner experience: choose an existing org
 * member, or invite someone new by name + email. The BE makes them a program
 * admin and notifies them (Teams + in-app, or email + in-app).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Dialog, MenuItem, Select, TextField, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DosenButton from '../ui/Buttons/DosenButton';
import { API } from '../../api';
import type { ProgramCollaborator } from '../../api';
import { useProgramContext } from '../../hooks/useProgramContext';
import { EDIT_LOCK_EXEMPT } from './EditLockGuard';

type Mode = 'existing' | 'new';
type OrgMember = { id: string; name: string; email: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(0, 0, 0, 0.70)',
    borderRadius: '6px',
    color: '#FDFDFD',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    '& fieldset': { borderColor: '#444444' },
    '&:hover fieldset': { borderColor: '#565656' },
    '&.Mui-focused fieldset': { borderColor: '#F79420' }
  },
  '& .MuiInputBase-input': { py: '10px', px: '12px' },
  '& .MuiInputLabel-root': { color: '#868686', fontFamily: 'Inter, sans-serif', fontSize: 14 },
  '& .MuiInputLabel-root.Mui-focused': { color: '#F79420' }
};

// The org-members payload is BE-enriched: `id` = Mongo _id, `name` from our DB.
const toOrgMember = (u: any): OrgMember => {
  const givenFamily = [u.given_name, u.family_name].filter(Boolean).join(' ');
  const rawName = String(u.name || '');
  return {
    id: String(u.id || u.user_id || ''),
    name: (!rawName.includes('@') && rawName) || givenFamily || rawName,
    email: String(u.email || '').toLowerCase()
  };
};

const ModeButton: React.FC<{ active: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  disabled,
  onClick,
  children
}) => (
  <Box
    component="button"
    type="button"
    disabled={disabled}
    onClick={onClick}
    sx={{
      flex: 1,
      py: '10px',
      borderRadius: '6px',
      border: `1px solid ${active ? '#F79420' : '#2A2A2A'}`,
      background: active ? 'rgba(247, 148, 32, 0.10)' : 'transparent',
      color: disabled ? '#565656' : '#FDFDFD',
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      fontWeight: 500,
      cursor: disabled ? 'default' : 'pointer',
      '&:hover': disabled ? {} : { borderColor: '#F79420' }
    }}
  >
    {children}
  </Box>
);

const InviteAdminDialog: React.FC<{
  open: boolean;
  grpId: string;
  programName?: string;
  collaborators: ProgramCollaborator[];
  onClose: () => void;
  onInvited: (collaborators: ProgramCollaborator[]) => void;
}> = ({ open, grpId, programName, collaborators, onClose, onInvited }) => {
  const { orgId } = useProgramContext();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [mode, setMode] = useState<Mode>('existing');
  const [selectedId, setSelectedId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedId('');
    setFirstName('');
    setLastName('');
    setEmail('');
    const tokens = localStorage.getItem('tokens') ? JSON.parse(localStorage.getItem('tokens') || '') : {};
    API.getUsersByOrgId(tokens.access_token || '', orgId)
      .then((res: any) => {
        const list: OrgMember[] = (Array.isArray(res?.data) ? res.data : []).map(toOrgMember).filter((m: OrgMember) => m.id);
        list.sort((a, b) => a.name.localeCompare(b.name));
        setMembers(list);
      })
      .catch(() => setMembers([]));
  }, [open, orgId]);

  // Anyone already on the program can't be picked again.
  const available = useMemo(() => {
    const taken = new Set(collaborators.flatMap((c) => [c.userId || '', c.email]).filter(Boolean));
    return members.filter((m) => !taken.has(m.id) && !taken.has(m.email));
  }, [members, collaborators]);

  useEffect(() => {
    if (open) setMode(available.length ? 'existing' : 'new');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const newValid = firstName.trim() && lastName.trim() && EMAIL_RE.test(email.trim());
  const canSubmit = !submitting && (mode === 'existing' ? Boolean(selectedId) : Boolean(newValid));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const data = await API.inviteProgramCollaborator(
        grpId,
        mode === 'existing'
          ? { userId: selectedId }
          : { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim().toLowerCase() }
      );
      const invitedName =
        mode === 'existing' ? available.find((m) => m.id === selectedId)?.name : `${firstName.trim()} ${lastName.trim()}`;
      toast.success(`${invitedName || 'Admin'} has been invited to ${programName || 'this program'}`);
      onInvited(Array.isArray(data?.collaborators) ? data.collaborators : collaborators);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'The invitation could not be sent. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      {...EDIT_LOCK_EXEMPT}
      onClose={submitting ? undefined : onClose}
      PaperProps={{
        sx: {
          background: '#1A1A1A',
          borderRadius: '12px',
          border: '1px solid #2A2A2A',
          p: 3,
          width: 440,
          maxWidth: 'calc(100% - 32px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }
      }}
    >
      <Box>
        <Typography sx={{ fontFamily: 'Inter', fontSize: 20, fontWeight: 700, color: '#FDFDFD' }}>Invite Admin</Typography>
        <Typography sx={{ fontFamily: 'Inter', fontSize: 13, color: '#B6B6B6', mt: 0.5, lineHeight: '20px' }}>
          They&rsquo;ll become an admin of {programName ? <strong>{programName}</strong> : 'this program'} and can edit its
          structure and content with you. Only one admin can be in the editor at a time.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <ModeButton active={mode === 'existing'} disabled={!available.length} onClick={() => setMode('existing')}>
          Choose Existing Person
        </ModeButton>
        <ModeButton active={mode === 'new'} onClick={() => setMode('new')}>
          Invite Someone New
        </ModeButton>
      </Box>

      {mode === 'existing' ? (
        <Select
          value={selectedId}
          onChange={(e) => setSelectedId(String(e.target.value))}
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          renderValue={(val) =>
            val ? (
              available.find((m) => m.id === val)?.name ?? ''
            ) : (
              <Box component="span" sx={{ color: '#868686' }}>
                Select a person...
              </Box>
            )
          }
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.70)',
            borderRadius: '6px',
            color: '#FDFDFD',
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444444' },
            '& .MuiSelect-select': { py: '10px', px: '12px' },
            '& .MuiSelect-icon': { color: '#868686', right: '12px' }
          }}
          MenuProps={{
            ...EDIT_LOCK_EXEMPT,
            PaperProps: {
              sx: {
                bgcolor: '#1C1C1C',
                border: '1px solid #2A2A2A',
                maxHeight: 320,
                '& .MuiMenuItem-root': { color: '#B6B6B6', fontFamily: 'Inter, sans-serif', fontSize: 14 }
              }
            }
          }}
        >
          {available.map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.name}
              {m.email && m.email !== m.name.toLowerCase() && (
                <Box component="span" sx={{ color: '#868686', ml: 1, fontSize: 12 }}>
                  {m.email}
                </Box>
              )}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              label="First name*"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              sx={{ ...fieldSx, flex: 1 }}
            />
            <TextField
              label="Last name*"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              sx={{ ...fieldSx, flex: 1 }}
            />
          </Box>
          <TextField
            label="Email*"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(email) && !EMAIL_RE.test(email.trim())}
            sx={fieldSx}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <DosenButton tone="basic" sizeVariant="lg" onClick={onClose} disabled={submitting}>
          Cancel
        </DosenButton>
        <DosenButton
          tone="cta"
          sizeVariant="lg"
          onClick={submit}
          disabled={!canSubmit}
          loading={submitting}
          loadingText="Sending..."
        >
          Send Invite
        </DosenButton>
      </Box>
    </Dialog>
  );
};

export default InviteAdminDialog;
